import { hrApiClient } from '@/lib/api';
import { IItemResponse, IListResponse } from '@/shared/base/base.dto';
import {
  AiGuidanceHistoryDto,
  AiUsageQuota,
  AiUsageResponse,
  CvAiCareerTrackDto,
} from '@/shared/cv/cv.ai.dto';
import {
  CvAiAboutResponse,
  CvAiEnhancePhotoResponse,
  CvAiSuggestHistoryResponse,
  CvAiSuggestResponse,
  CvAiUploadResponse,
} from '@/types/cv';
import { AxiosError } from 'axios';

const emptyAiUsageQuota = (): AiUsageQuota => ({
  total: 0,
  avialable: 0,
});

function extractCvAiSuggestText(
  payload: string | CvAiSuggestResponse | undefined
): string {
  if (typeof payload === 'string') {
    return payload;
  }

  if (payload && typeof payload === 'object') {
    return payload.critique ?? '';
  }

  return '';
}

function buildCvAiSuggestHistoryResponse(
  payload: AiGuidanceHistoryDto[] | undefined
): CvAiSuggestHistoryResponse {
  if (!payload?.length) {
    return { resume_text: '' };
  }

  const latest = [...payload].sort((a, b) => b.time - a.time || b.id - a.id)[0];

  return {
    resume_text: latest.guidance,
    time: latest.time,
  };
}

const CAREER_REQUEST_TIMEOUT_MS = 150_000;

function handleCvAiCareerError(error: unknown): never {
  return handleCvAiRequestError(
    error,
    'Таны AI ашиглан карьер төлөвлөгөө боловсруулах эрх дууссан байна.'
  );
}

function handleCvAiSuggestError(error: unknown): never {
  return handleCvAiRequestError(
    error,
    'Таны CV зөвлөмж үүсгэх эрх дууссан байна.'
  );
}

function handleCvAiRequestError(error: unknown, usageMessage: string): never {
  if (
    error instanceof Error &&
    (error.name === 'CanceledError' || error.name === 'AbortError')
  ) {
    throw new Error('Request timed out after 2.5 minutes');
  }

  const status = (error as AxiosError)?.response?.status;
  if (status === 402) {
    const usageError = new Error(usageMessage) as Error & { status?: number };
    usageError.status = 402;
    throw usageError;
  }

  throw error;
}

export const fetchCvAiAbout = async (lang: number): Promise<string> => {
  const { data } = await hrApiClient.get<
    IItemResponse<CvAiAboutResponse | string>
  >('/cv/ai/about', {
    params: { lang },
  });

  const payload = data.data;

  if (typeof payload === 'string') {
    return payload;
  }

  return payload?.about_me ?? '';
};

export const uploadCvAi = async (
  formData: FormData
): Promise<CvAiUploadResponse> => {
  const { data } = await hrApiClient.post<IItemResponse<CvAiUploadResponse>>(
    '/cv/ai/upload',
    formData
  );

  return data.data ?? {};
};

export const fetchCvAiEnhancePhoto = async (
  formData: FormData
): Promise<CvAiEnhancePhotoResponse> => {
  const { data } = await hrApiClient.post<CvAiEnhancePhotoResponse>(
    '/cv/ai/enhance-photo',
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    }
  );

  return data;
};

export const fetchCvAiUsage = async (): Promise<AiUsageResponse> => {
  const { data } = await hrApiClient.get<AiUsageResponse>('/cv/ai/usage');

  return {
    id: data.id,
    userId: data.userId,
    aiAbout: data.aiAbout ?? emptyAiUsageQuota(),
    aiCareer: data.aiCareer ?? emptyAiUsageQuota(),
    aiSuggest: data.aiSuggest ?? emptyAiUsageQuota(),
    aiEnhancePhoto: data.aiEnhancePhoto ?? emptyAiUsageQuota(),
    aiAnalyzePhoto: data.aiAnalyzePhoto ?? emptyAiUsageQuota(),
  };
};

export const fetchCvAiCareer = async (
  lang: number
): Promise<IListResponse<CvAiCareerTrackDto>> => {
  const controller = new AbortController();
  const timeout = setTimeout(
    () => controller.abort(),
    CAREER_REQUEST_TIMEOUT_MS
  );

  try {
    const { data } = await hrApiClient.get<IListResponse<CvAiCareerTrackDto>>(
      '/cv/ai/career',
      {
        params: { lang },
        signal: controller.signal,
        timeout: CAREER_REQUEST_TIMEOUT_MS,
      }
    );

    return data;
  } catch (error) {
    handleCvAiCareerError(error);
  } finally {
    clearTimeout(timeout);
  }
};

export const fetchCvAiCareerHistory = async (): Promise<
  IListResponse<CvAiCareerTrackDto>
> => {
  const controller = new AbortController();
  const timeout = setTimeout(
    () => controller.abort(),
    CAREER_REQUEST_TIMEOUT_MS
  );

  try {
    const { data } = await hrApiClient.get<IListResponse<CvAiCareerTrackDto>>(
      '/cv/ai/career-history',
      {
        signal: controller.signal,
        timeout: CAREER_REQUEST_TIMEOUT_MS,
      }
    );

    return data;
  } catch (error) {
    handleCvAiCareerError(error);
  } finally {
    clearTimeout(timeout);
  }
};

export const fetchCvAiSuggestHistory =
  async (): Promise<CvAiSuggestHistoryResponse> => {
    const { data } = await hrApiClient.get<
      IItemResponse<AiGuidanceHistoryDto[]>
    >('/cv/ai/suggest-history');

    return buildCvAiSuggestHistoryResponse(data.data);
  };

export const fetchCvAiSuggest = async (
  lang: number
): Promise<CvAiSuggestHistoryResponse> => {
  const controller = new AbortController();
  const timeout = setTimeout(
    () => controller.abort(),
    CAREER_REQUEST_TIMEOUT_MS
  );

  try {
    const { data } = await hrApiClient.get<
      IItemResponse<CvAiSuggestResponse | string>
    >('/cv/ai/suggest', {
      params: { lang },
      signal: controller.signal,
      timeout: CAREER_REQUEST_TIMEOUT_MS,
    });

    return {
      resume_text: extractCvAiSuggestText(data.data),
    };
  } catch (error) {
    handleCvAiSuggestError(error);
  } finally {
    clearTimeout(timeout);
  }
};
